import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useTheme } from '../../context/ThemeContext';
import { billingAPI } from '../../services/api';

interface PaymentMethodFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setAsDefault, setSetAsDefault] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create SetupIntent on the server
      const setupResponse = await billingAPI.createSetupIntent();
      const clientSecret = setupResponse.data.client_secret;

      // Confirm the SetupIntent with Stripe
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        throw new Error('Card element not found');
      }

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardNumberElement,
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (setupIntent?.payment_method) {
        // Save payment method to our backend
        await billingAPI.addPaymentMethod(
          setupIntent.payment_method as string,
          setAsDefault
        );
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || t('billing.errors.addPaymentMethod'));
    } finally {
      setLoading(false);
    }
  };

  const elementStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: isDark ? '#e2e8f0' : '#374151',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        '::placeholder': {
          color: isDark ? '#64748b' : '#9CA3AF',
        },
      },
      invalid: {
        color: '#EF4444',
        iconColor: '#EF4444',
      },
    },
  };

  const inputContainerClass = `border rounded-xl px-4 py-3 transition-colors focus-within:ring-2 focus-within:ring-cyan-500 focus-within:border-transparent ${
    isDark ? 'border-slate-600 bg-slate-700/50' : 'border-gray-300 bg-white'
  }`;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
      {/* Card Number */}
      <div>
        <label htmlFor="cardNumber" className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
          {t('billing.cardNumber')}
        </label>
        <div className={inputContainerClass}>
          <CardNumberElement
            id="cardNumber"
            options={{
              ...elementStyle,
              showIcon: true,
            }}
          />
        </div>
      </div>

      {/* Expiry and CVC */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="cardExpiry" className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            {t('billing.expiryDate')}
          </label>
          <div className={inputContainerClass}>
            <CardExpiryElement
              id="cardExpiry"
              options={elementStyle}
            />
          </div>
        </div>
        <div>
          <label htmlFor="cardCvc" className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            {t('billing.cvc')}
          </label>
          <div className={inputContainerClass}>
            <CardCvcElement
              id="cardCvc"
              options={elementStyle}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="setAsDefault"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
          className={`h-4 w-4 rounded ${isDark ? 'bg-slate-700 border-slate-600 text-cyan-500' : 'text-blue-600 border-gray-300'}`}
        />
        <label htmlFor="setAsDefault" className={`ml-2 text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
          {t('billing.setAsDefault')}
        </label>
      </div>

      {error && (
        <div className={`px-4 py-3 rounded-lg text-sm ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {error}
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className={`px-4 py-2 rounded-lg transition-colors ${isDark ? 'text-slate-300 bg-slate-700 hover:bg-slate-600' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {loading ? t('common.saving') : t('billing.addCard')}
        </button>
      </div>
    </form>
  );
};

export default PaymentMethodForm;
